/**
 * Snapping - Handles geometric calculations for orthogonal drawing
 *
 * This module provides snapping functionality to create orthogonal polygons with:
 * - Local constraints: Each segment is parallel or perpendicular to the previous (0°/90°/180°/270°)
 * - Global constraints: Option to enforce 90° angles at specific vertices
 *
 * Note on coordinate system:
 * We use longitude/latitude scaling (cosine correction) rather than full cartesian projection.
 * This is a simple approximation that works well for small geographic areas and avoids
 * the complexity of projection transformations like Web Mercator.
 */

// =============================================================================
// Constants
// =============================================================================

/**
 * Minimum distance threshold to avoid degenerate cases in geometric calculations
 * Used when checking if points are too close together to form valid angles
 */
const MIN_DISTANCE_THRESHOLD = 0.0001;

/**
 * Deviation threshold multiplier for global snap validation
 * Global snap is only applied if deviation is less than this percentage of segment length
 */
const GLOBAL_SNAP_DEVIATION_THRESHOLD = 0.5; // 50% of previous segment

/**
 * Tolerance for validating that global snap creates a reasonable angle
 * Allows up to this many degrees deviation from 90°
 */
const GLOBAL_SNAP_ANGLE_TOLERANCE = 45; // degrees

// =============================================================================
// Types
// =============================================================================

interface SnapResult {
  lat: number;
  lng: number;
}

type Vector2D = [number, number];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Applies longitude scaling correction based on latitude
 * Accounts for the fact that longitude lines converge at the poles
 */
function getLongitudeScale(latitude: number): number {
  return Math.max(Math.cos((latitude * Math.PI) / 180), 1e-6);
}

/**
 * Calculates the average latitude from multiple points
 */
function getAverageLatitude(...coords: Array<Coordinate | SnapResult>): number {
  const latitudes = coords.map((c) => (Array.isArray(c) ? c[1] : c.lat));
  return latitudes.reduce((sum, lat) => sum + lat, 0) / latitudes.length;
}

/**
 * Calculates the dot product of two 2D vectors
 */
function dotProduct(v: Vector2D, u: Vector2D): number {
  return v[0] * u[0] + v[1] * u[1];
}

// =============================================================================
// Vertex Utilities
// =============================================================================

/**
 * Extracts the last real vertex, handling closed geometries
 * If first and last vertices are identical (closed polygon), returns the previous vertex
 */
export function getLastVertex(points: Coordinate[]): Coordinate | null {
  if (!points || points.length === 0) return null;

  const first = points[0];
  const last = points[points.length - 1];

  // Check if geometry is closed
  if (points.length > 1 && first[0] === last[0] && first[1] === last[1]) {
    return points[points.length - 2] ?? null;
  }

  return last;
}

/**
 * Counts real vertices in the polygon, handling closed geometries
 */
export function getRealVertexCount(points: Coordinate[]): number {
  if (points.length === 1) return 1;

  const first = points[0];
  const last = points[points.length - 1];

  // If closed (first === last), subtract 1
  return first[0] === last[0] && first[1] === last[1]
    ? points.length - 1
    : points.length;
}

/**
 * Gets the previous real vertex, handling closed geometries
 */
export function getPreviousVertex(points: Coordinate[]): Coordinate | null {
  const first = points[0];
  const last = points[points.length - 1];

  // If closed, get the 3rd from last (or 2nd from last as fallback)
  if (first[0] === last[0] && first[1] === last[1]) {
    return points[points.length - 3] ?? points[points.length - 2] ?? null;
  }

  // If open, get the 2nd from last
  return points[points.length - 2] ?? null;
}

// =============================================================================
// Snapping Functions
// =============================================================================

/**
 * Snaps position to axis-aligned directions (horizontal or vertical)
 * Used for the first segment when there's no previous direction to follow
 */
function snapToAxis(
  lastVertex: Coordinate,
  currentLng: number,
  currentLat: number,
): SnapResult {
  const lonScale = getLongitudeScale(lastVertex[1]);

  const dLat = Math.abs(currentLat - lastVertex[1]);
  const dLng = Math.abs((currentLng - lastVertex[0]) * lonScale);

  // Choose the dominant direction
  return dLat < dLng
    ? { lat: lastVertex[1], lng: currentLng } // Horizontal
    : { lat: currentLat, lng: lastVertex[0] }; // Vertical
}

/**
 * Snaps position to be EITHER parallel OR perpendicular to the previous segment
 * This creates orthogonal constraints (0°/90°/180°/270°)
 *
 * Algorithm:
 * 1. Calculate the direction of the previous segment (secondLast -> last)
 * 2. Calculate the perpendicular direction (90° rotation)
 * 3. Project the mouse vector onto both directions
 * 4. Choose the direction with the larger projection (user's intent)
 * 5. Snap the position along that chosen direction
 */
function snapToParallelOrPerpendicular(
  secondLastVertex: Coordinate,
  lastVertex: Coordinate,
  currentLng: number,
  currentLat: number,
): SnapResult {
  const avgLat = getAverageLatitude(secondLastVertex, lastVertex, [
    currentLng,
    currentLat,
  ]);
  const lonScale = getLongitudeScale(avgLat);

  // Calculate previous segment direction vector (prev -> last)
  const segmentVectorX = (lastVertex[0] - secondLastVertex[0]) * lonScale;
  const segmentVectorY = lastVertex[1] - secondLastVertex[1];
  const segmentLength = Math.hypot(segmentVectorX, segmentVectorY);

  if (segmentLength === 0) {
    return { lat: currentLat, lng: currentLng };
  }

  // Normalize segment vector to get parallel unit vector
  const parallelUnitVectorX = segmentVectorX / segmentLength;
  const parallelUnitVectorY = segmentVectorY / segmentLength;

  // Calculate perpendicular unit vector (90° counter-clockwise rotation)
  const perpendicularUnitVectorX = -parallelUnitVectorY;
  const perpendicularUnitVectorY = parallelUnitVectorX;

  // Calculate mouse vector relative to last vertex
  const mouseVectorX = (currentLng - lastVertex[0]) * lonScale;
  const mouseVectorY = currentLat - lastVertex[1];

  // Project mouse vector onto both directions to determine user intent
  // The larger projection indicates which direction the user wants
  const distanceAlongParallelVector = Math.abs(
    dotProduct(
      [mouseVectorX, mouseVectorY],
      [parallelUnitVectorX, parallelUnitVectorY],
    ),
  );
  const distanceAlongPerpendicularVector = Math.abs(
    dotProduct(
      [mouseVectorX, mouseVectorY],
      [perpendicularUnitVectorX, perpendicularUnitVectorY],
    ),
  );

  const useParallel =
    distanceAlongParallelVector > distanceAlongPerpendicularVector;

  // Choose basis vector (parallel or perpendicular) based on larger projection
  const basisVectorX = useParallel
    ? parallelUnitVectorX
    : perpendicularUnitVectorX;
  const basisVectorY = useParallel
    ? parallelUnitVectorY
    : perpendicularUnitVectorY;

  // Project mouse vector onto chosen basis (this time with sign)
  const distanceAlongBasisVector = dotProduct(
    [mouseVectorX, mouseVectorY],
    [basisVectorX, basisVectorY],
  );

  // Calculate snapped position along the chosen direction
  return {
    lat: lastVertex[1] + basisVectorY * distanceAlongBasisVector,
    lng: lastVertex[0] + (basisVectorX * distanceAlongBasisVector) / lonScale,
  };
}

/**
 * Checks if the angle at vertex C (formed by A -> C -> B) is approximately 90°
 *
 * Vertex naming:
 * - A: firstVertex
 * - B: lastVertex
 * - C: currentPosition
 */
export function isRightAngle(
  vertexA: Coordinate,
  vertexB: Coordinate,
  vertexC: SnapResult,
  toleranceDegrees: number = 5,
): boolean {
  const [ax, ay] = vertexA;
  const [bx, by] = vertexB;
  const { lng: cx, lat: cy } = vertexC;

  const avgLat = getAverageLatitude(vertexA, vertexB, vertexC);
  const lonScale = getLongitudeScale(avgLat);

  // Vector AC: first -> current
  const vectorAC_X = (cx - ax) * lonScale;
  const vectorAC_Y = cy - ay;

  // Vector CB: current -> last
  const vectorCB_X = (bx - cx) * lonScale;
  const vectorCB_Y = by - cy;

  const lengthAC = Math.hypot(vectorAC_X, vectorAC_Y);
  const lengthCB = Math.hypot(vectorCB_X, vectorCB_Y);

  if (lengthAC === 0 || lengthCB === 0) return false;

  // Calculate angle using dot product: cos(θ) = (AC · CB) / (|AC| * |CB|)
  const dot = dotProduct([vectorAC_X, vectorAC_Y], [vectorCB_X, vectorCB_Y]);
  const cosAngle = dot / (lengthAC * lengthCB);
  const angleDegrees =
    (Math.acos(Math.max(-1, Math.min(1, cosAngle))) * 180) / Math.PI;

  return Math.abs(angleDegrees - 90) <= toleranceDegrees;
}

/**
 * Adjusts the snapped position to create an exact 90° angle
 * while maintaining the orthogonal direction from the previous segment
 *
 * Vertex naming:
 * - A: firstVertex (vertexA)
 * - B: lastVertex (vertexB)
 * - C: locallySnappedPosition (approximate vertexC)
 *
 * Goal: Find the exact position of C on the line through B in direction (B->C)
 * such that angle ACB = 90°
 *
 * Mathematical derivation:
 * - Let C = B + t·dir, where dir is the unit direction from B to C
 * - For 90° at C, we need: (A->C) ⊥ (C->B)
 * - This means: (A->C) · (C->B) = 0
 * - Expanding: (C - A) · (B - C) = 0
 * - Substituting C = B + t·dir:
 *   ((B + t·dir) - A) · (B - (B + t·dir)) = 0
 *   ((B - A) + t·dir) · (-t·dir) = 0
 *   -(B - A)·(t·dir) - t²·dir·dir = 0
 *   -t·(B - A)·dir - t² = 0  (since |dir| = 1)
 *   t·[-(B - A)·dir - t] = 0
 * - Non-trivial solution: t = -(B - A)·dir
 */
function snapToExactRightAngle(
  vertexA: Coordinate,
  vertexB: Coordinate,
  vertexC_approx: SnapResult,
): SnapResult | null {
  const avgLat = getAverageLatitude(vertexA, vertexB, vertexC_approx);
  const lonScale = getLongitudeScale(avgLat);

  // Direction vector from B to C (already orthogonal by local snap)
  const directionVectorX = (vertexC_approx.lng - vertexB[0]) * lonScale;
  const directionVectorY = vertexC_approx.lat - vertexB[1];
  const directionLength = Math.hypot(directionVectorX, directionVectorY);

  if (directionLength === 0) return null;

  // Normalize to get unit direction vector
  const unitDirectionVectorX = directionVectorX / directionLength;
  const unitDirectionVectorY = directionVectorY / directionLength;

  // Vector from A to B
  const vectorAB_X = (vertexB[0] - vertexA[0]) * lonScale;
  const vectorAB_Y = vertexB[1] - vertexA[1];

  // Calculate the distance t along the direction that creates exactly 90°
  // From derivation: t = -(B - A) · dir = -(vectorAB) · unitDirection
  const dot = dotProduct(
    [vectorAB_X, vectorAB_Y],
    [unitDirectionVectorX, unitDirectionVectorY],
  );
  const t = -dot;

  // Skip if distance is too small (degenerate case)
  if (Math.abs(t) < MIN_DISTANCE_THRESHOLD) return null;

  // Calculate exact position: C = B + t·dir
  return {
    lat: vertexB[1] + unitDirectionVectorY * t,
    lng: vertexB[0] + (unitDirectionVectorX * t) / lonScale,
  };
}

// =============================================================================
// Main Snapping Function
// =============================================================================

/**
 * Main snapping function that calculates the orthogonally constrained position
 *
 * @param currentLng - Current mouse longitude
 * @param currentLat - Current mouse latitude
 * @param points - Array of existing vertices
 * @returns Snapped position
 */
export function calculateSnappedPosition(
  currentLng: number,
  currentLat: number,
  points: Coordinate[],
): SnapResult {
  if (!points?.length) {
    return { lat: currentLat, lng: currentLng };
  }

  const lastVertex = getLastVertex(points);
  if (!lastVertex) {
    return { lat: currentLat, lng: currentLng };
  }

  const realCount = getRealVertexCount(points);

  // First segment: snap to axis-aligned directions
  if (realCount === 1) {
    return snapToAxis(lastVertex, currentLng, currentLat);
  }

  // Get previous vertex for directional snapping
  const secondLastVertex = getPreviousVertex(points);
  if (!secondLastVertex) {
    return { lat: currentLat, lng: currentLng };
  }

  // Apply LOCAL orthogonal snapping (secondLast -> last -> current)
  // This ensures each new segment is parallel or perpendicular to the previous one
  const locallySnapped = snapToParallelOrPerpendicular(
    secondLastVertex,
    lastVertex,
    currentLng,
    currentLat,
  );

  // Try GLOBAL right angle snapping when we have enough vertices (3+)
  // "Global" because it considers the first vertex to close the shape with 90° angles
  // This is particularly useful when closing polygons to ensure the last angle is 90°
  if (realCount > 2) {
    const firstVertex = points[0];

    // Always attempt global snap when orthogonal mode is enabled
    // This allows snapping even when previous vertices weren't placed orthogonally
    const rightAngleSnapped = snapToExactRightAngle(
      firstVertex,
      lastVertex,
      locallySnapped,
    );

    // Validation logic:
    // The global snap might move the point significantly from the local snap position.
    // We need to validate that:
    // 1. The resulting angle is reasonable (within 45° of 90°)
    // 2. The deviation from local snap isn't too large (< 50% of previous segment)
    // This prevents wild position changes that would break user expectation
    if (rightAngleSnapped) {
      // Calculate the deviation between local and global snap positions
      const lonScale = getLongitudeScale(lastVertex[1]);

      // Components of the deviation vector (rightAngleSnapped - locallySnapped)
      const deltaLat = Math.abs(rightAngleSnapped.lat - locallySnapped.lat);
      const deltaLng =
        Math.abs(rightAngleSnapped.lng - locallySnapped.lng) * lonScale;

      // Magnitude of deviation
      const deviation = Math.hypot(deltaLng, deltaLat);

      // Calculate reference length (previous segment for comparison)
      const segmentVectorX = (lastVertex[0] - secondLastVertex[0]) * lonScale;
      const segmentVectorY = lastVertex[1] - secondLastVertex[1];
      const segmentLength = Math.hypot(segmentVectorX, segmentVectorY);

      // Check if the resulting angle is reasonable (not too far from 90°)
      // Use a larger tolerance since we want to be aggressive with snapping
      const angleIsReasonable = isRightAngle(
        firstVertex,
        lastVertex,
        rightAngleSnapped,
        GLOBAL_SNAP_ANGLE_TOLERANCE,
      );

      // Only apply global snap if both conditions are met:
      // 1. The angle is reasonable (within 45° of 90°)
      // 2. Deviation is acceptable (< 50% of segment length for aggressive snapping)
      const deviationThreshold =
        segmentLength * GLOBAL_SNAP_DEVIATION_THRESHOLD;

      if (angleIsReasonable && deviation < deviationThreshold) {
        return rightAngleSnapped;
      }
    }
  }

  return locallySnapped;
}
